import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only'
import 'error-cause-polyfill/auto'

import { createRoot } from 'react-dom/client'
import { setupRootStyle, startRootStyleWatchers } from 'utils/rootStyle'
import createOpenChatButton from 'utils/createOpenButton'
import createInvisibleInput from 'utils/createInvisibleInput'
import {
  mobileBreakpoint,
  rootId,
  UrlHashesFragments,
  UrlGetParams,
  WidgetModes,
  scriptId,
  chatLaunchAttrName,
} from 'consts'
import TRACKING_INFORMATION from 'utils/trackingInformation'
import shouldChatWidgetOpen from 'utils/shouldChatWidgetOpen'
import {
  clearConsentMessageClosed,
  clearSessionStorage,
  getBackendSession,
  getIsRasaHandoffActive,
  localStoragePrefix,
  setBackendSession,
  setLaunchIntentData,
} from 'utils/sessionStorage'
import { AvailableCampaigns } from 'entities/Config'
import getTheme from 'utils/getTheme'
import fetchUrl from 'utils/fetchUrl'
import Abort from 'utils/abort'
import getAdServerEventsUrls from 'utils/getAdServerEventsUrls'
import {
  getTenant,
  getTenantLanguage,
  getGlobalConfig,
} from 'utils/getTenantSpecificValues'
import dataLayerPushEvent from 'utils/dataLayerPushEvent'
import checkRequiredProps from 'utils/checkRequiredProps'
import checkConsent from 'utils/checkConsent'
import { sendError } from 'api'
import matchTargeting from 'utils/matchTargeting'
import getWidgetMode from 'utils/getWidgetMode'
import getConfig from 'utils/getConfig'
import getTrackingCampaignId from 'utils/getTrackingCampaignId'
import { addStoredEventListener } from 'utils/storedEventListener'
import getFeatureFlagCookie, {
  isFeatureFlagEnabled,
} from 'utils/getFeatureFlagCookie'
import type { Root } from 'react-dom/client'
import type { StoredEventListenerKeys } from 'consts'
import type { EventsUrls } from 'utils/getAdServerEventsUrls'
import type { ThemeConfig, ThemesListConfig } from 'entities/Config'

export interface RootElement extends HTMLElement {
  setCssProperties: (theme: ThemeConfig) => void
}

interface UCEvent extends Event {
  detail?: {
    action?:
      | 'onInitialPageLoad'
      | 'onAcceptAllServices'
      | 'onDenyAllServices'
      | 'onUpdateServices'
      | 'onSessionRestored'
    event?: string
    ucCategory?: {
      functional: boolean
    }
  }
}

interface UCViewChangedEvent extends Event {
  detail?: {
    view: 'NONE' | 'FIRST_LAYER' | 'PRIVACY_BUTTON' | 'SECOND_LAYER'
  }
}

let root: Root | null = null
let initialised = false
let fullWidgetLoaded = false
let checkingConsent = false
let waitingForConsentFormAction = false
const featureFlagCookie = getFeatureFlagCookie()
const MAX_POLLING_DURATION_IN_MS = 500
const widgetMode = getWidgetMode()

const cleanupOldLocalStorage = () => {
  // Check if sessionStorage is empty or has no sae-conversational keys
  const hasConversationalKeys = Object.keys(sessionStorage).some((key) =>
    key.startsWith('sae-conversational')
  )

  if (!hasConversationalKeys) {
    // Clean up any session-related items from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(localStoragePrefix)) {
        localStorage.removeItem(key)
      }
    })
  }
}

cleanupOldLocalStorage()

const handleStorageEvent = (event: StorageEvent) => {
  if (event?.key?.startsWith(localStoragePrefix)) {
    const actualKey = event.key.replace(localStoragePrefix, '')

    if (event.newValue === null) {
      sessionStorage.removeItem(actualKey)
    } else {
      sessionStorage.setItem(actualKey, event.newValue)
    }
  }
}

if (widgetMode !== WidgetModes.MobileApp) {
  // Removed in SyncContainer
  addStoredEventListener<StoredEventListenerKeys>({
    type: 'storage',
    key: 'sessionStorageSync',
    target: window,
    listener: handleStorageEvent,
  })
}

const registerUsercentricsContentUpdate = () => {
  const globalConfig = getGlobalConfig()

  if (!globalConfig?.ucPopupTitle && !globalConfig?.ucPopupContent) {
    return
  }

  let pollId: number

  const poll = () => {
    const usercentricsRoot = document.getElementById('usercentrics-root')
    const shadowRoot = usercentricsRoot?.shadowRoot

    if (shadowRoot) {
      if (globalConfig?.ucPopupTitle) {
        const header = shadowRoot.getElementById('uc-heading-title')

        if (header) {
          header.innerText = globalConfig.ucPopupTitle
        }
      }

      if (globalConfig?.ucPopupContent) {
        const content = shadowRoot.getElementById('uc-show-more')

        if (content) {
          content.innerHTML = `<div>${globalConfig.ucPopupContent}</div>`
        }
      }

      cancelAnimationFrame(pollId)
    }

    pollId = requestAnimationFrame(poll)
    return null
  }

  const startPolling = () => {
    pollId = requestAnimationFrame(poll)
    setTimeout(() => {
      cancelAnimationFrame(pollId)
    }, MAX_POLLING_DURATION_IN_MS)
  }

  window.addEventListener('UC_UI_VIEW_CHANGED', (event: UCViewChangedEvent) => {
    if (checkingConsent && event.detail?.view === 'SECOND_LAYER') {
      startPolling()
    }
  })
}

const openConsentForm = async () => {
  checkingConsent = true
  dataLayerPushEvent('consentWidgetClicked')

  if (window?.UC_UI?.showSecondLayer) {
    await window.UC_UI.showSecondLayer()
  } else {
    waitingForConsentFormAction = true
  }
}

const init = () => {
  if (initialised) {
    return
  }
  initialised = true
  const globalConfig = getGlobalConfig()

  const container = document.getElementById(rootId) as RootElement
  const tenant = getTenant()
  const language = getTenantLanguage()

  const urlHash = window.location.hash
  const getParams = Array.from(
    new URLSearchParams(window.location.search).keys()
  )

  if (getParams.includes(UrlGetParams.HideChatbot)) {
    return
  }

  registerUsercentricsContentUpdate()

  const withCounter =
    window.innerWidth <= mobileBreakpoint &&
    (urlHash.indexOf(UrlHashesFragments.SupportChatNotifications) !== -1 ||
      getParams.includes(UrlGetParams.SupportChatNotifications))

  container.setCssProperties = (theme: ThemeConfig) => {
    const { cssProperties, palette, backend, id } = theme
    const currentBackend = getBackendSession()
    const isRasaHandoffActive =
      isFeatureFlagEnabled(featureFlagCookie, 'rasa-order-flow', 'rasa') &&
      id === AvailableCampaigns.default &&
      getIsRasaHandoffActive() &&
      // TODO Remove once overflow migration is completed
      globalConfig?.orderFlowMigration

    const activeBackend = isRasaHandoffActive ? currentBackend : backend

    if (!isRasaHandoffActive && currentBackend !== activeBackend) {
      clearSessionStorage()
    }

    setBackendSession(activeBackend || 'backend-1')

    const cssProps = {
      ...palette,
      ...cssProperties,
    }

    Object.entries(cssProps).forEach(([prop, value]) => {
      container.style.setProperty(`--${prop}`, value)
    })
  }

  const matchPaths = (paths: Array<string>) => {
    const pathName = window.location.pathname
    const isMatching: boolean = paths.some((path: string) =>
      pathName.startsWith(path)
    )

    return isMatching
  }

  const loadAnalyticsTracker = async () => {
    /* webpackMode: "lazy" */

    const analytics = await import(
      /* webpackChunkName: "analytics" */ './utils/analytics'
    )

    return analytics.default
  }

  const getThemeConfigs = (config: any) => {
    const { global, default: defaultConfig, ...tenantConfigs } = config

    if (global == null) {
      throw new Error('Config invalid: `global` object is missing')
    }

    if (tenant == null) {
      throw new Error('Config invalid: `tenant` is missing')
    }

    if (language == null) {
      throw new Error('Config invalid: `language` is missing')
    }

    if (global?.[tenant]?.[language]?.showWidget == null) {
      throw new Error('Config invalid: `global` object missing required fields')
    }

    return {
      [AvailableCampaigns.default]: defaultConfig,
      ...tenantConfigs?.[tenant]?.[language],
    }
  }

  // eslint-disable-next-line complexity
  const initialiseConfig = async (): Promise<{
    showWidget: boolean
    themeConfig: ThemeConfig
    availableConfigs: ThemesListConfig
  } | null> => {
    try {
      const config = getConfig()

      if (!config) {
        throw new Error('Config not defined')
      }

      checkRequiredProps(config, ['global', 'default'])
      checkRequiredProps(config.global?.[tenant]?.[language], ['showWidget'])

      const { global } = config

      const themeConfigs = getThemeConfigs(config)
      const themeConfig = getTheme(themeConfigs)

      checkRequiredProps(themeConfig, ['id', 'backend'])

      container.setCssProperties(themeConfig)

      const globalTenantLanguageConfig = global?.[tenant]?.[language]
      let showWidget = false
      const response = {
        showWidget,
        themeConfig,
        availableConfigs: themeConfigs,
      }

      if (globalTenantLanguageConfig) {
        const { hiddenPages = [], chatConsultOnlyPages = [] } =
          globalTenantLanguageConfig

        if (typeof globalTenantLanguageConfig.showWidget === 'boolean') {
          showWidget = globalTenantLanguageConfig.showWidget
        } else if (
          'targeting' in globalTenantLanguageConfig.showWidget &&
          globalTenantLanguageConfig.showWidget.targeting.length
        ) {
          const { targeting } = globalTenantLanguageConfig.showWidget

          showWidget = matchTargeting(targeting)
        }

        if (showWidget && hiddenPages.length) {
          const isHiddenPageMatched = matchPaths(hiddenPages)

          if (isHiddenPageMatched) {
            showWidget = false
          }
        }

        const isChatConsultOnlyPagesMatched = matchPaths(chatConsultOnlyPages)
        if (
          isChatConsultOnlyPagesMatched &&
          showWidget &&
          chatConsultOnlyPages.length
        ) {
          showWidget =
            isChatConsultOnlyPagesMatched &&
            themeConfig.id !== AvailableCampaigns.default
        }
      } else {
        throw new Error('Invalid config: `global` is not defined')
      }

      registerUsercentricsContentUpdate()

      return {
        ...response,
        showWidget,
      }
    } catch (error) {
      sendError((error as Error).message, {
        language,
        tenant,
      })
      // eslint-disable-next-line
      console.error({ error })
      return null
    }
  }

  // iOS will not deploy virtual keyboard if we focus an input outside of
  // user ineracion event. But it does not hide the keyboard when keyboard
  // is already out and we jump between inputs.
  // We create this (invisible) input so that keyboard gets shown and
  // later when message input is rendered, we switch to that.
  const input = createInvisibleInput()

  const loadCheckUserlikeState = async () => {
    /* webpackMode: "lazy" */

    const { default: checkUserlikeState } = await import(
      /* webpackChunkName: "checkUserlikeState" */ './utils/userlike'
    )

    return checkUserlikeState
  }

  const chatConsultInitialAnalytics = async (campaign: AvailableCampaigns) => {
    const customAnalytics = await loadAnalyticsTracker()
    customAnalytics.track('consult-offered', {
      category: 'chat-consult',
      label: campaign,
    })
  }

  const loadAndOpenApplication = async ({
    themeConfig,
    availableConfigs,
    viewUrl,
    clickUrl,
  }: {
    themeConfig: ThemeConfig
    availableConfigs: ThemesListConfig
    viewUrl?: string
    clickUrl?: string
  }) => {
    clearConsentMessageClosed()
    if (fullWidgetLoaded) {
      return
    }

    fullWidgetLoaded = true
    input.disabled = true

    const customAnalytics = await loadAnalyticsTracker()
    customAnalytics.track(TRACKING_INFORMATION.widgetOpenedByClick.action, {
      category: TRACKING_INFORMATION.widgetOpenedByClick.category,
      label: TRACKING_INFORMATION.widgetOpenedByClick.label,
    })

    dataLayerPushEvent('chatbotOpened', {
      'cb-campaign': getTrackingCampaignId(themeConfig),
    })

    if (root) {
      root.unmount()
    }

    root = createRoot(document.getElementById(rootId) as RootElement)

    /**
     * Uncomment next line and change `disabled` to `false` if you need initial focus on input fields
     * for phones
     */
    // input.focus()

    /* webpackMode: "lazy" */
    ;(await import('./init')).default({
      root,
      container,
      themeConfig,
      availableConfigs,
      invisibleDummyInput: input,
      mode: widgetMode,
      viewUrl,
      clickUrl,
    })

    setTimeout(() => {
      input.disabled = true
    }, 50)
  }

  const loadMobileApp = async ({
    themeConfig,
    availableConfigs,
  }: {
    themeConfig: ThemeConfig
    availableConfigs: ThemesListConfig
  }) => {
    loadAndOpenApplication({ themeConfig, availableConfigs })
    setupRootStyle(container)
    startRootStyleWatchers(container)
  }

  const loadWebApp = async ({
    themeConfig,
    availableConfigs,
  }: {
    themeConfig: ThemeConfig
    availableConfigs: ThemesListConfig
  }) => {
    const isConsentGiven = checkConsent()
    const button = createOpenChatButton(themeConfig, {
      withCounter,
      isConsentGiven,
    })
    const consentEnabled = globalConfig?.checkConsentEnabled

    if (consentEnabled && !isConsentGiven) {
      dataLayerPushEvent('consentWidgetImpressed')
    }

    const chatWindowOpensOnLoad = shouldChatWidgetOpen()
    const { clickUrl, impressionUrl, viewUrl, placeUrl }: EventsUrls =
      await getAdServerEventsUrls(themeConfig.adServerProperties)

    if (placeUrl) {
      void fetchUrl(placeUrl)
    }

    button.addEventListener('click', async () => {
      if (consentEnabled && !isConsentGiven) {
        await openConsentForm()
        return
      }

      button.disabled = true

      if (impressionUrl) {
        void fetchUrl(impressionUrl)
      }

      await loadAndOpenApplication({
        themeConfig,
        availableConfigs,
        viewUrl,
        clickUrl,
      })
    })

    if (chatWindowOpensOnLoad) {
      const customAnalytics = await loadAnalyticsTracker()
      customAnalytics.track(TRACKING_INFORMATION.widgetOpenedByHash.action, {
        category: TRACKING_INFORMATION.widgetOpenedByHash.category,
        label: TRACKING_INFORMATION.widgetOpenedByHash.label,
      })
    }

    if (chatWindowOpensOnLoad && !withCounter) {
      loadAndOpenApplication({ themeConfig, availableConfigs, clickUrl })
    }

    // Removed in UserEntryPoint
    addStoredEventListener<StoredEventListenerKeys>({
      key: 'launchChatFromButtonClick',
      target: window,
      type: 'click',
      listener: async (event: Event) => {
        const targetElement = (event.target as Element)?.closest(
          `[${chatLaunchAttrName}]`
        )

        const intentData = targetElement?.getAttribute(chatLaunchAttrName)

        if (targetElement && intentData) {
          setLaunchIntentData(intentData)

          await loadAndOpenApplication({
            themeConfig,
            availableConfigs,
            clickUrl,
            viewUrl,
          })
        }
      },
    })

    window.addEventListener(
      'UC_UI_VIEW_CHANGED',
      (event: UCViewChangedEvent) => {
        if (event.detail?.view === 'NONE') {
          checkingConsent = false
        }
      }
    )

    window.addEventListener('ucEvent', async (e: UCEvent) => {
      if (checkingConsent && e.detail?.ucCategory?.functional) {
        await loadAndOpenApplication({
          themeConfig,
          availableConfigs,
          clickUrl,
        })

        dataLayerPushEvent('consentWidgetOpened', {
          'cb-campaign': getTrackingCampaignId(themeConfig),
        })
      }
    })

    setupRootStyle(container)
    startRootStyleWatchers(container)

    if (
      themeConfig.id !== AvailableCampaigns.default &&
      !getParams.includes(UrlGetParams.ViewProduct)
    ) {
      chatConsultInitialAnalytics(themeConfig.id)
    }

    container.appendChild(button)
  }

  const initialiseApp = async () => {
    const config = await initialiseConfig()

    if (!config?.showWidget) {
      return
    }

    const checkUserlikeState = await loadCheckUserlikeState()
    await checkUserlikeState()

    const { themeConfig, availableConfigs } = config

    switch (widgetMode) {
      case WidgetModes.MobileApp:
        loadMobileApp({ themeConfig, availableConfigs })
        break

      default:
        loadWebApp({ themeConfig, availableConfigs })
    }
  }

  initialiseApp()
}
window._saeConversationalInit = init

const loadScript = () => {
  const existingScript = document.getElementById(scriptId)
  if (existingScript) {
    existingScript.remove()
  }

  if (window._saeConversationalConfig) {
    init()
    return
  }
  const script = document.createElement('script')
  script.id = scriptId
  script.type = 'text/javascript'
  script.async = true
  script.src = process.env.REACT_APP_CLIENT_CONFIG!
  script.onerror = (error) => {
    // eslint-disable-next-line no-console
    console.error(
      `Error loading script: ${process.env.REACT_APP_CLIENT_CONFIG!}`,
      error
    )
  }
  document.head.appendChild(script)
}

loadScript()

const reloadApp = () => {
  if (root) {
    root.unmount()
    root = null
  }

  const container = document.getElementById(rootId) as RootElement
  container.remove()

  initialised = false
  fullWidgetLoaded = false
  checkingConsent = false

  const rootElement = document.createElement('div')

  rootElement.setAttribute('id', rootId)
  document.body.appendChild(rootElement)

  loadScript()
}

window.addEventListener('ucEvent', (e: UCEvent) => {
  if (e.detail?.action === 'onInitialPageLoad' && waitingForConsentFormAction) {
    openConsentForm()
    waitingForConsentFormAction = false
  }

  if (!checkingConsent && e.detail?.event === 'consent_status') {
    reloadApp()
  }
})

window.addEventListener('gtmDataLoaded', () => {
  reloadApp()
})

window.addEventListener('beforeunload', () => {
  Abort.getInstance().abort()
})
